import { createContext, useContext } from 'react'

/**
 * What state the player is in, and therefore what the bar says.
 *
 * A 404 and a 503 are different sentences and both are more useful than
 * "could not load audio": one says this meeting has no recording stored, the
 * other says no meeting on this deployment can be played — which is an
 * operator's problem and not the reader's.
 */
export type PlayerState =
  | 'loading'
  | 'ready'
  /** The source loaded and then failed — usually an expired signed URL. */
  | 'media-error'
  /** 404: this meeting has no stored audio (never admitted, or not yet transcribed). */
  | 'unavailable'
  /** 503: the deployment has no media bucket wired. */
  | 'not-configured'
  /**
   * 403: this person's role does not hold `audio:download`.
   *
   * Distinct from 'failed' because a refusal is not a fault, and the two send
   * the reader to different places — one to ask an admin for the permission,
   * the other to ask whether something is broken. Before migration 040 this
   * was every member and viewer in the workspace, and they were all shown
   * "Could not load the recording." over a Try again button that could never
   * succeed. A workspace can still produce it by narrowing a role.
   */
  | 'forbidden'
  | 'failed'

/**
 * The meeting player, shared by everything that drives or follows it.
 *
 * TWO AUDIENCES, ONE CONTEXT. The first seven members are the FOLLOWER API —
 * what the transcript needs to highlight the playing line and to seek. The
 * rest are the BAR's own controls, which only `MeetingPlayerBar` reads. They
 * are one context because they are one player: a second context for the bar
 * would be a second source of truth for `isPlaying`, and the two would drift
 * the first time somebody added a keyboard shortcut.
 *
 * WHY A CONTEXT AND NOT PROPS. The bar sits above the tabs and the transcript
 * inside one, and the tab is unmounted every time somebody switches to the
 * summary and back. Threading `currentTime` through the page as state would
 * work and would re-render the whole page four times a second while anything
 * is playing.
 *
 * WHY `currentTime` IS STILL STATE AND NOT A REF. It has to re-render the
 * transcript for the active line to move, which is the entire feature. The
 * cost is bounded by the player's own publish interval, not by how often the
 * browser fires `timeupdate` (~4 Hz, and unspecified).
 */
export interface Player {
  /** Seconds. The playhead, whether or not anything is playing. */
  currentTime: number
  /** Seconds, or null before metadata has loaded. */
  duration: number | null
  isPlaying: boolean
  /** Move the playhead. Does NOT start playback — clicking a timecode while
   *  paused should move you there, not start the room talking. */
  seek: (seconds: number) => void
  /** Move the playhead AND play. What a "play from here" affordance means. */
  playFrom: (seconds: number) => void
  toggle: () => void
  /**
   * False when this meeting has no audio, the deployment has none, or the
   * source has not loaded yet. A follower must not offer "play from here" on
   * a player that cannot play — a control that does nothing when clicked is
   * worse than one that is plainly inert.
   */
  isReady: boolean

  /* --- the bar's own controls ------------------------------------------- */
  state: PlayerState
  muted: boolean
  speed: number
  skip: (delta: number) => void
  toggleMute: () => void
  cycleSpeed: () => void
  /** Sign a fresh URL and try again. The recovery for an expired credential. */
  retry: () => void
}

export const PlayerContext = createContext<Player | null>(null)

/**
 * The player if one is mounted, otherwise null.
 *
 * Deliberately nullable rather than throwing. `TranscriptPanel` is rendered
 * where there is no player and never will be, and a hook that threw would
 * make the transcript unrenderable there — a far worse failure than a
 * timecode that only highlights.
 */
export function usePlayer(): Player | null {
  return useContext(PlayerContext)
}
